<script setup lang="ts">
import { computed } from "vue";
import { BookOpenText, Clapperboard, Code2, Film, Palette, PanelsTopLeft } from "lucide-vue-next";
import type { ExecutionTable } from "@/types";

const props = defineProps<{
  tables?: ExecutionTable[];
  compact?: boolean;
  skillId?: string;
}>();

const presentations = {
  "ai-video-production": { label: "分镜执行规格", note: "镜头、画面、台词、声音与验收", icon: Clapperboard, theme: "video" },
  "drama-production": { label: "剧情制作规格", note: "剧情、角色、镜头与连续性", icon: Film, theme: "drama" },
  "design-service": { label: "设计需求规格", note: "内容、画面、版式、尺寸与交付", icon: Palette, theme: "design" },
  "website-build": { label: "产品功能规格", note: "模块、功能、字段、状态与权限", icon: PanelsTopLeft, theme: "website" },
  "development-service": { label: "技术实现合同", note: "接口、数据、校验、异常与验收", icon: Code2, theme: "development" },
  "article-content": { label: "内容生产规格", note: "结构、事实、语气、篇幅与发布", icon: BookOpenText, theme: "article" },
} as const;

const presentation = computed(() =>
  presentations[props.skillId as keyof typeof presentations] || {
    label: "执行需求规格",
    note: "客户确认内容与可核查验收要求",
    icon: BookOpenText,
    theme: "general",
  },
);
</script>

<template>
  <section v-if="tables?.length" class="execution-tables" :class="[presentation.theme, { compact }]">
    <header v-if="!compact" class="artifact-head">
      <component :is="presentation.icon" :size="17" />
      <div><b>{{ presentation.label }}</b><small>{{ presentation.note }}</small></div>
    </header>
    <article v-for="table in tables" :key="table.id">
      <h4>{{ table.title }}</h4>
      <div class="table-scroll">
        <table>
          <thead><tr><th v-for="column in table.columns" :key="column">{{ column }}</th></tr></thead>
          <tbody>
            <tr v-for="(row, rowIndex) in table.rows" :key="rowIndex">
              <td v-for="(_, columnIndex) in table.columns" :key="columnIndex">{{ row[columnIndex] || "" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<style scoped>
.execution-tables{--artifact:#52656b;display:grid;gap:14px;margin:14px 0}.execution-tables.video{--artifact:#2e6e72}.execution-tables.drama{--artifact:#72533f}.execution-tables.design{--artifact:#8a4e61}.execution-tables.website{--artifact:#336b54}.execution-tables.development{--artifact:#555d83}.execution-tables.article{--artifact:#7a6331}.artifact-head{display:flex;align-items:center;gap:8px;padding-bottom:8px;border-bottom:2px solid var(--artifact);color:var(--artifact)}.artifact-head div{display:grid;gap:1px}.artifact-head b{font-size:13px}.artifact-head small{color:var(--muted);font-size:11px}.execution-tables article{min-width:0}.execution-tables h4{margin:0 0 7px;color:var(--ink);font-size:14px;letter-spacing:0}.table-scroll{width:100%;overflow-x:auto;overscroll-behavior-x:contain;border:1px solid var(--line);-webkit-overflow-scrolling:touch}.table-scroll table{width:100%;border-collapse:collapse}.table-scroll th,.table-scroll td{min-width:120px;padding:9px 10px;border-right:1px solid var(--line);border-bottom:1px solid var(--line);vertical-align:top;text-align:left;white-space:normal;overflow-wrap:anywhere}.table-scroll th{border-top:2px solid var(--artifact);background:#eef3f3;color:var(--ink);font-size:12px}.table-scroll td{background:#fff;color:var(--ink);font-size:13px;line-height:1.55}.table-scroll tr:last-child td{border-bottom:0}.table-scroll th:last-child,.table-scroll td:last-child{border-right:0}.compact{margin:10px 0}.compact .table-scroll th,.compact .table-scroll td{padding:7px 8px;font-size:12px}@media(max-width:700px){.execution-tables{margin:10px 0}.artifact-head{position:sticky;left:0}.table-scroll th,.table-scroll td{min-width:132px;padding:8px;font-size:12px}}
</style>
